// One andon, on the leader's Andon tab. Adapted from the Andon app's
// `FloorCard`, in the one layout the shop floor draws.
//
// WHAT IS ON THE FACE, AND WHY
//
// The AN number, the stars, the type and the place on one line; the title on
// the next; the clock and the owner right-aligned as a pair. The whole card
// opens the thread — a gloved thumb should not have to find a link — and the
// text on it is selectable, because the AN number is the identifier people
// quote across a factory and paste into everything else.
//
// AND ONE VERB, FOR THE LEADER. The Andon app's phone list carried no buttons
// — every action was one tap away on the thread, in front of the context — and
// its wall board carried the stop question. The frame draws a third thing: a
// leader reading the "Nobody yet" group wants to take an andon from the list,
// so "Take it" sits on the face of an unowned card and opens the ETA sheet. It
// is the one verb a list can carry honestly, because taking is a choice of
// time and the sheet asks for it; nothing else on the thread is a single tap.
//
// WHAT DID NOT COME ACROSS. The column variant, the drag between columns, the
// per-card confetti and the follow-up tick belonged to the Andon app's board,
// which the shop floor does not have.

import { andonAvatarUrl } from '@/api/andon/client';
import type { AndonAlertVM } from '@/api/andon/types';
import { useT } from '@/i18n/useT';
import { ackLabel } from '@/lib/actions';
import { badgeOf, floorClock, type Badge } from '@/lib/floor';
import { selectingWithin } from '@/lib/selection';
import { STAR_COLOR, starLabel } from '@/lib/severity';
import { OwnerAvatar } from './primitives';

const TONE: Record<Badge['tone'], { fg: string; bg: string }> = {
  danger: { fg: 'var(--danger)', bg: 'rgba(255,90,95,.14)' },
  warn: { fg: 'var(--warn)', bg: 'rgba(240,161,58,.14)' },
  pos: { fg: 'var(--pos)', bg: 'rgba(55,199,126,.16)' },
  quiet: { fg: 'var(--sub)', bg: 'var(--surface2)' },
};

export function FloorCard({
  alert,
  now,
  onOpen,
  onTake,
}: {
  alert: AndonAlertVM;
  now: number;
  onOpen: () => void;
  /**
   * Take it from the list. Absent on a PIN session — taking writes an Odoo
   * user onto the record, and the service account names nobody — and absent
   * on an andon somebody already holds, where the honest verb is a takeover
   * and that is a deliberate act made from the thread.
   */
  onTake?: (() => void) | undefined;
}) {
  const t = useT();
  const badge = badgeOf(alert);
  const sla = floorClock(alert, now, t.agoT);
  const canTake = onTake != null && alert.open && alert.owner.unowned;

  /**
   * Open the thread — unless the tap was the end of a drag that selected some
   * text on this card. See lib/selection.ts for why it asks the browser rather
   * than measuring how far the pointer moved.
   */
  const tapOpen = (e: { currentTarget: Node }) => {
    if (selectingWithin(e.currentTarget)) return;
    onOpen();
  };

  return (
    <div
      style={{
        borderRadius: 15,
        background: 'var(--surface)',
        border: `1px solid ${alert.sla.breaching ? 'var(--danger)' : 'var(--line)'}`,
        borderLeft: `3px solid ${STAR_COLOR[alert.stars]}`,
        padding: '11px 13px',
        animation: 'ipRise .3s ease',
      }}
    >
      {/* A role rather than a <button>: a button's contents are the LABEL OF A
          CONTROL as far as the browser is concerned, so it resists a drag
          across them — and the andon's own text has to be selectable. The
          keyboard behaviour a button would have given is written out below. */}
      <div
        role="button"
        tabIndex={0}
        className="press-soft"
        onClick={tapOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen();
          }
        }}
        aria-label={`${alert.qa}. ${alert.title}. ${t.openT}`}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, textAlign: 'left', cursor: 'pointer' }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4, minWidth: 0 }}>
            {/* The andon number, and it leads. `flex: none`, so the type and
                station truncate before this does: a truncated AN number is
                not an AN number. */}
            <span className="numeric selectable" style={{ font: "700 10.5px 'Space Grotesk',sans-serif", color: 'var(--sub)', flex: 'none' }}>
              {alert.qa}
            </span>
            <span style={{ font: "700 11px 'Space Grotesk',sans-serif", letterSpacing: '.04em', color: STAR_COLOR[alert.stars], flex: 'none' }}>{starLabel(alert.stars)}</span>
            <span className="selectable" style={{ font: '600 10.5px Manrope,sans-serif', color: 'var(--sub)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {alert.typeLabel} · {alert.workcentre}
            </span>
            {badge && (
              <span
                style={{
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: TONE[badge.tone].bg,
                  color: TONE[badge.tone].fg,
                  font: '700 8.5px Manrope,sans-serif',
                  letterSpacing: '.06em',
                  whiteSpace: 'nowrap',
                  textTransform: 'uppercase',
                  // The badge is the one thing on this row that may give way.
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: 'none',
                }}
              >
                {t[badge.text]}
              </span>
            )}
            {alert.mine && (
              <span style={{ flex: 'none', padding: '1px 5px', borderRadius: 4, background: 'var(--surface2)', color: 'var(--sub)', font: '700 8.5px Manrope,sans-serif', letterSpacing: '.06em', textTransform: 'uppercase' }}>
                {t.yoursT}
              </span>
            )}
          </span>
          <span
            className="selectable"
            style={{
              font: '700 13.5px/1.35 Manrope,sans-serif',
              color: 'var(--ink)',
              overflow: 'hidden',
              // Two lines, then an ellipsis. A card that grows to three breaks
              // the rhythm the list depends on for five andons to fit above
              // the fold.
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {alert.title}
          </span>
        </span>
        <span style={{ textAlign: 'right', flex: 'none' }}>
          <span className="numeric" style={{ display: 'block', font: "700 13px 'Space Grotesk',sans-serif", color: sla.color }}>
            {sla.text}
          </span>
          <span style={{ display: 'block', marginTop: 5, marginLeft: 'auto', width: 26 }}>
            <OwnerAvatar name={alert.owner.name} size={26} photoUrl={andonAvatarUrl(alert.owner)} />
          </span>
        </span>
      </div>
      {canTake && (
        <button
          type="button"
          className="press-soft"
          onClick={onTake}
          style={{
            marginTop: 10,
            width: '100%',
            minHeight: 40,
            borderRadius: 11,
            background: 'var(--pos)',
            color: '#fff',
            border: 'none',
            font: "700 12.5px 'Space Grotesk',sans-serif",
          }}
        >
          {ackLabel(alert, t)} — {t.etaPickT}
        </button>
      )}
    </div>
  );
}
